import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import api from '../../services/api';
import { useLanguage } from '../../state/language/LanguageProvider';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  role: string;
  tenantName: string;
  createdAt: string;
}

interface Tenant {
  id: string;
  name: string;
  adminEmail: string;
}

const PasswordManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'users' | 'tenants'>('users');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [modalType, setModalType] = useState<'user' | 'tenant'>('user');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersResponse, tenantsResponse] = await Promise.all([
        api.get('/users/admin/users'),
        api.get('/tenants')
      ]);
      
      setUsers(usersResponse.data.data);
      setTenants(tenantsResponse.data.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      setError(t('passwordManagement.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetUserPassword = (user: User) => {
    setSelectedUser(user);
    setSelectedTenant(null);
    setModalType('user');
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordModal(true);
    setError('');
    setSuccess('');
  };

  const handleSetTenantAdminPassword = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setSelectedUser(null);
    setModalType('tenant');
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordModal(true);
    setError('');
    setSuccess('');
  };

  const handleSubmitPassword = async () => {
    if (newPassword !== confirmPassword) {
      setError(t('passwordManagement.passwordsDoNotMatch'));
      return;
    }

    if (newPassword.length < 8) {
      setError(t('passwordManagement.passwordTooShort'));
      return;
    }

    try {
      if (modalType === 'user' && selectedUser) {
        await api.post('/users/admin/reset-password', {
          userId: selectedUser.id,
          newPassword
        });
        setSuccess(t('passwordManagement.passwordResetSuccess', { email: selectedUser.email }));
      } else if (modalType === 'tenant' && selectedTenant) {
        await api.post('/users/admin/set-tenant-admin-password', {
          tenantId: selectedTenant.id,
          newPassword
        });
        setSuccess(t('passwordManagement.tenantAdminPasswordSetSuccess', { tenantName: selectedTenant.name }));
      }
      
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setSelectedUser(null);
      setSelectedTenant(null);
    } catch (error: any) {
      setError(error.response?.data?.message || t('passwordManagement.passwordUpdateFailed'));
    }
  };

  const closeModal = () => {
    setShowPasswordModal(false);
    setNewPassword('');
    setConfirmPassword('');
    setSelectedUser(null);
    setSelectedTenant(null);
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-secondary-bg)] p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center text-[var(--color-secondary-text)]">{t('passwordManagement.loadingData')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)] p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--color-surface-text)] mb-2">{t('passwordManagement.title')}</h1>
          <p className="text-[var(--color-secondary-text)]">{t('passwordManagement.description')}</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-[var(--color-success)]/10 border border-[var(--color-success)]/20 text-[var(--color-success)] rounded">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 text-[var(--color-error)] rounded">
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-6 bg-[var(--color-primary-bg)] rounded-lg p-1">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'users'
                ? 'bg-[var(--color-secondary-bg)] text-[var(--color-primary-text)]'
                : 'text-[var(--color-secondary-text)] hover:text-[var(--color-primary-text)]'
            }`}
          >
            {t('passwordManagement.userPasswords')}
          </button>
          <button
            onClick={() => setActiveTab('tenants')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'tenants'
                ? 'bg-[var(--color-secondary-bg)] text-[var(--color-primary-text)]'
                : 'text-[var(--color-secondary-text)] hover:text-[var(--color-primary-text)]'
            }`}
          >
            {t('passwordManagement.tenantAdminPasswords')}
          </button>
        </div>

        {/* User Passwords Tab */}
        {activeTab === 'users' && (
          <Card>
            <div className="p-6">
              <h2 className="text-xl font-semibold text-[var(--color-surface-text)] mb-4">{t('passwordManagement.userPasswordManagement')}</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[var(--color-border)]">
                  <thead className="bg-[var(--color-secondary-bg)]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('passwordManagement.user')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('passwordManagement.role')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('passwordManagement.tenant')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('passwordManagement.status')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('passwordManagement.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-[var(--color-surface-bg)] divide-y divide-[var(--color-border)]">
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-[var(--color-surface-text)]">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-[var(--color-secondary-text)]">{user.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-[var(--color-info)]/10 text-[var(--color-info)]">
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                          {user.tenantName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.status === 'ACTIVE' 
                              ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]' 
                              : 'bg-[var(--color-error)]/10 text-[var(--color-error)]'
                          }`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Button
                            onClick={() => handleResetUserPassword(user)}
                            className="bg-[var(--color-primary-bg)] hover:opacity-90 text-[var(--color-primary-text)]"
                          >
                            {t('passwordManagement.resetPassword')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        )}

        {/* Tenant Admin Passwords Tab */}
        {activeTab === 'tenants' && (
          <Card>
            <div className="p-6">
              <h2 className="text-xl font-semibold text-[var(--color-surface-text)] mb-4">{t('passwordManagement.tenantAdminPasswordManagement')}</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[var(--color-border)]">
                  <thead className="bg-[var(--color-secondary-bg)]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('passwordManagement.tenant')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('passwordManagement.adminEmail')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-secondary-text)] uppercase tracking-wider">
                        {t('passwordManagement.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-[var(--color-surface-bg)] divide-y divide-[var(--color-border)]">
                    {tenants.map((tenant) => (
                      <tr key={tenant.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-[var(--color-surface-text)]">{tenant.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-surface-text)]">
                          {tenant.adminEmail}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Button
                            onClick={() => handleSetTenantAdminPassword(tenant)}
                            className="bg-[var(--color-success)] hover:opacity-90 text-[var(--color-accent-text)]"
                          >
                            {t('passwordManagement.setAdminPassword')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        )}

        {/* Password Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-[var(--color-surface-text)] bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border border-[var(--color-border)] w-96 shadow-lg rounded-md bg-[var(--color-surface-bg)]">
              <div className="mt-3">
                                  <h3 className="text-lg font-medium text-[var(--color-surface-text)] mb-4">
                    {modalType === 'user' 
                      ? `${t('passwordManagement.resetPassword')} for ${selectedUser?.firstName} ${selectedUser?.lastName}`
                      : `${t('passwordManagement.setAdminPassword')} for ${selectedTenant?.name}`
                    }
                  </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                      {t('passwordManagement.newPassword')}
                    </label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t('passwordManagement.enterNewPassword')}
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-1">
                      {t('passwordManagement.confirmPassword')}
                    </label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t('passwordManagement.confirmNewPassword')}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <Button
                    onClick={closeModal}
                    className="bg-[var(--color-secondary-bg)] hover:bg-[var(--color-border)] text-[var(--color-secondary-text)]"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleSubmitPassword}
                    className="bg-[var(--color-primary-bg)] hover:opacity-90 text-[var(--color-primary-text)]"
                  >
                    {modalType === 'user' ? t('passwordManagement.resetPassword') : t('passwordManagement.setAdminPassword')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordManagementPage;
